import { useEffect, useRef, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Badge } from '@/components/ui/badge';
import { MapPin, TrendingUp } from 'lucide-react';

interface LocationData {
  latitude: number;
  longitude: number;
  city: string;
  country: string;
  count: number;
}

interface GeographyMapProps {
  locations: LocationData[];
}

const MAPBOX_TOKEN = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN || '';

// USA center coordinates
const USA_CENTER: [number, number] = [-98.5795, 39.8283];
const USA_ZOOM = 3.5;

export function GeographyMap({ locations }: GeographyMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);

  // Calculate top regions with detailed stats
  const regionStats = useMemo(() => {
    const totalSessions = locations.reduce((sum, loc) => sum + loc.count, 0);
    
    // Group by state/region (extract from city name or use country)
    const regionMap = new Map<string, { 
      sessions: number; 
      cities: string[]; 
      topCity: string;
      topCityCount: number;
    }>();

    // Group by country first
    const countryMap = new Map<string, LocationData[]>();
    locations.forEach(loc => {
      const existing = countryMap.get(loc.country) || [];
      existing.push(loc);
      countryMap.set(loc.country, existing);
    });

    // Build region stats
    const stats = locations
      .sort((a, b) => b.count - a.count)
      .slice(0, 10)
      .map(loc => ({
        city: loc.city,
        country: loc.country,
        sessions: loc.count,
        percentage: totalSessions > 0 ? Math.round((loc.count / totalSessions) * 100) : 0,
        latitude: loc.latitude,
        longitude: loc.longitude,
      }));

    // Country breakdown
    const countryStats = Array.from(countryMap.entries())
      .map(([country, locs]) => ({
        country,
        sessions: locs.reduce((sum, l) => sum + l.count, 0),
        cities: locs.length,
      }))
      .sort((a, b) => b.sessions - a.sessions);

    return { 
      topCities: stats, 
      countryStats, 
      totalSessions,
      usaPercentage: countryStats.find(c => c.country === 'United States')?.sessions 
        ? Math.round((countryStats.find(c => c.country === 'United States')!.sessions / totalSessions) * 100)
        : 0
    };
  }, [locations]);

  useEffect(() => {
    if (!mapContainer.current || !MAPBOX_TOKEN) return;

    mapboxgl.accessToken = MAPBOX_TOKEN;

    // Determine initial view - prioritize USA if we have US locations
    const hasUSLocations = locations.some(loc => loc.country === 'United States');
    const initialCenter = hasUSLocations ? USA_CENTER : [0, 20] as [number, number];
    const initialZoom = hasUSLocations ? USA_ZOOM : 1.5;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: initialCenter,
      zoom: initialZoom,
    });

    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Add markers for each location with enhanced styling
    locations.forEach((location) => {
      if (location.latitude && location.longitude) {
        // Calculate marker size based on session count
        const maxCount = Math.max(...locations.map(l => l.count));
        const sizeRatio = location.count / maxCount;
        const size = Math.max(24, Math.min(60, 24 + (sizeRatio * 36)));
        
        // Create a custom marker with gradient based on intensity
        const el = document.createElement('div');
        el.className = 'custom-marker';
        el.style.width = `${size}px`;
        el.style.height = `${size}px`;
        el.style.borderRadius = '50%';
        el.style.cursor = 'pointer';
        el.style.display = 'flex';
        el.style.alignItems = 'center';
        el.style.justifyContent = 'center';
        el.style.fontSize = '10px';
        el.style.fontWeight = 'bold';
        el.style.color = 'white';
        el.style.textShadow = '0 1px 2px rgba(0,0,0,0.5)';
        
        // Color gradient from green (low) to orange (high)
        const hue = Math.round(160 - (sizeRatio * 120)); // 160 (green) to 40 (orange)
        el.style.backgroundColor = `hsla(${hue}, 84%, 45%, 0.8)`;
        el.style.border = `2px solid hsla(${hue}, 84%, 55%, 1)`;
        el.style.boxShadow = `0 0 ${size / 2}px hsla(${hue}, 84%, 50%, 0.4)`;
        
        // Show count on marker if large enough
        if (size >= 32) {
          el.textContent = location.count.toString();
        }

        new mapboxgl.Marker(el)
          .setLngLat([location.longitude, location.latitude])
          .setPopup(
            new mapboxgl.Popup({ offset: 25, className: 'custom-popup' }).setHTML(
              `<div style="padding: 8px; font-family: system-ui, sans-serif;">
                <h3 style="font-weight: 600; font-size: 14px; margin: 0 0 4px 0;">${location.city}</h3>
                <p style="font-size: 12px; color: #888; margin: 0 0 8px 0;">${location.country}</p>
                <div style="display: flex; align-items: center; gap: 8px;">
                  <span style="font-size: 20px; font-weight: 700; color: hsl(160, 84%, 39%);">${location.count}</span>
                  <span style="font-size: 11px; color: #666;">session${location.count > 1 ? 's' : ''}</span>
                </div>
                <p style="font-size: 10px; color: #666; margin-top: 4px;">
                  ${Math.round((location.count / regionStats.totalSessions) * 100)}% of total traffic
                </p>
              </div>`
            )
          )
          .addTo(map.current!);
      }
    });

    return () => {
      map.current?.remove();
    };
  }, [locations, regionStats.totalSessions]);

  if (!MAPBOX_TOKEN) {
    return (
      <div className="flex items-center justify-center h-[400px] bg-muted rounded-lg">
        <p className="text-muted-foreground">
          Mapbox token not configured. Add VITE_MAPBOX_PUBLIC_TOKEN to enable map.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Map Container */}
      <div ref={mapContainer} className="h-[400px] rounded-lg" />
      
      {/* Top Regions Detail Table */}
      {regionStats.topCities.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <MapPin className="h-4 w-4 text-primary" />
              Top Regions by Traffic
            </h4>
            {regionStats.usaPercentage > 0 && (
              <Badge variant="outline" className="text-xs">
                🇺🇸 {regionStats.usaPercentage}% USA
              </Badge>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {regionStats.topCities.slice(0, 8).map((city, idx) => (
              <div 
                key={`${city.city}-${city.country}`}
                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-muted-foreground w-5">
                    #{idx + 1}
                  </span>
                  <div>
                    <p className="font-medium text-sm">{city.city}</p>
                    <p className="text-xs text-muted-foreground">{city.country}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-bold text-sm">{city.sessions}</p>
                  <p className="text-xs text-muted-foreground">{city.percentage}%</p>
                </div>
              </div>
            ))}
          </div>

          {/* Country Summary */}
          {regionStats.countryStats.length > 1 && (
            <div className="pt-3 border-t">
              <p className="text-xs text-muted-foreground mb-2">Traffic by Country</p>
              <div className="flex flex-wrap gap-2">
                {regionStats.countryStats.slice(0, 5).map(country => (
                  <Badge key={country.country} variant="secondary" className="text-xs">
                    {country.country}: {country.sessions} ({country.cities} {country.cities === 1 ? 'city' : 'cities'})
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
